import { useEffect, useRef, useState } from "react";
import { ArchiveBoxIcon, XMarkIcon } from "@heroicons/react/24/outline";
import Button from "../common/Button";
import Dropdown from "../Settings/Dropdown";

interface Props {
  open: boolean;
  onClose: () => void;
  onExport: (format: "pdf" | "docx") => Promise<void>;
}

// The full AI record as a ZIP, for disclosure. The only choice is which
// format the three narrative documents inside take; everything else is
// always included.
const ExportDiscoveryModal: React.FC<Props> = ({ open, onClose, onExport }) => {
  const [format, setFormat] = useState<"pdf" | "docx">("pdf");
  const [exporting, setExporting] = useState(false);
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const el = dialogRef.current;
    if (!el) return;
    if (open && !el.open) el.showModal();
    else if (!open && el.open) el.close();
  }, [open]);

  const handleExport = async () => {
    setExporting(true);
    try {
      await onExport(format);
      onClose();
    } catch {
      // toast shown by the caller
    } finally {
      setExporting(false);
    }
  };

  const contents: [string, string][] = [
    ["Investigation summary", "The current brief, every section."],
    ["Lead sheet", "Every flagged moment: confirmed, new, and dismissed with reasons."],
    ["Timeline", "The full chronology, all severities, hidden items included."],
    ["Transcripts", "Plain-text transcript of every recording (privileged ones omitted)."],
    ["Chat history", "Every assistant conversation, in full."],
    ["Spreadsheets", "leads.csv, files.csv, audit-log.csv."],
    ["Raw record", "raw-data.json, the machine-readable record behind all of the above."],
  ];

  return (
    <dialog ref={dialogRef} className="modal" onCancel={(e) => { e.preventDefault(); onClose(); }}>
      <div className="modal-box max-w-lg p-0 overflow-hidden rounded-2xl">
        <div className="flex items-start gap-3 px-6 pt-6 pb-4">
          <div className="size-10 shrink-0 rounded-xl bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300 flex items-center justify-center">
            <ArchiveBoxIcon className="size-6" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-lg font-bold text-base-content leading-tight">Export discovery package</h3>
            <p className="text-sm text-base-content/60 mt-0.5">
              The complete AI record as a ZIP, including dismissed leads and every question asked of the assistant. Built for disclosure, not for the case file.
            </p>
          </div>
          <button type="button" onClick={onClose} className="p-1 rounded-md text-base-content/50 hover:bg-base-200 hover:text-base-content" title="Close">
            <XMarkIcon className="size-5" />
          </button>
        </div>
        <div className="px-6">
          <p className="text-xs font-semibold text-base-content/70 mb-1.5">Contents</p>
          <ul className="rounded-lg border border-base-300 divide-y divide-base-300 bg-base-100">
            {contents.map(([name, what]) => (
              <li key={name} className="px-3 py-2">
                <span className="block text-sm text-base-content">{name}</span>
                <span className="block text-[11px] text-base-content/50">{what}</span>
              </li>
            ))}
          </ul>
          <p className="mt-1.5 text-[11px] text-base-content/50">The format below applies to the summary, lead sheet, and timeline. Transcripts are plain text either way.</p>
        </div>
        <div className="px-6 py-5 flex items-center gap-2">
          <Dropdown size="sm" dense direction="top" menuAlign="start" contentClassName="!z-40" options={[{ value: "pdf", label: "PDF" }, { value: "docx", label: "Word (.docx)" }]} value={format} onChange={(v) => setFormat(v as "pdf" | "docx")} />
          <div className="ml-auto flex items-center gap-2">
            <Button variant="secondary" size="sm" onClick={onClose} disabled={exporting}>
              Cancel
            </Button>
            <Button variant="primary" size="sm" onClick={handleExport} disabled={exporting}>
              {exporting ? "Building…" : "Download ZIP"}
            </Button>
          </div>
        </div>
      </div>
      <form method="dialog" className="modal-backdrop">
        <button onClick={onClose}>close</button>
      </form>
    </dialog>
  );
};

export default ExportDiscoveryModal;
